import { API, SensorType } from "common";
import { useEffect, useState } from "react";

const LIST_SENSORS = /* GraphQL */ `
  query ListSensors($limit: Int, $nextToken: String) {
    listSensors(limit: $limit, nextToken: $nextToken) {
      id
      hubId
      serialNumber
      type
      name
      status
      location
      sublocation
      lastReported
    }
  }
`;

const LIST_SENSOR_ALERTS = /* GraphQL */ `
  query ListSensorAlerts($limit: Int, $nextToken: String) {
    listSensorAlerts(limit: $limit, nextToken: $nextToken) {
      id
      timestamp
      sensorId
      hubId
      alertType
      severity
      message
      status
      acknowledgedAt
      acknowledgedBy
    }
  }
`;

const LIST_SERVICE_REQUESTS = /* GraphQL */ `
  query ListServiceRequests($limit: Int, $nextToken: String) {
    listServiceRequests(limit: $limit, nextToken: $nextToken) {
      id
      customerId
      customerName
      customerEmail
      customerPhone
      hubId
      sensorId
      alertId
      serviceCompanyId
      type
      priority
      status
      description
      scheduledDate
      completedDate
      createdAt
      updatedAt
    }
  }
`;

const SENSOR_TYPE_COLORS: Record<string, string> = {
  [SensorType.HVAC]: "#3B82F6",
  [SensorType.LEAK_DETECTOR]: "#10B981",
  [SensorType.POWER]: "#F59E0B",
  [SensorType.AIR_QUALITY]: "#EF4444",
  [SensorType.WATER_QUALITY]: "#F97316",
};
const DEFAULT_SENSOR_TYPE_COLOR = "#6B7280";

export const useSensorErrorAnalytics = () => {
  const [sensorErrorCount, setSensorErrorCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = (await API.graphql({
        query: LIST_SENSORS,
        variables: { limit: 1000 },
      })) as any;
      const sensors = result.data?.listSensors || [];

      const errorCount = sensors.filter(
        (sensor: any) =>
          sensor.status === "ERROR" || sensor.status === "OFFLINE",
      ).length;

      setSensorErrorCount(errorCount);
    } catch (err) {
      console.error("Error fetching sensor error analytics:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch sensor errors",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    sensorErrorCount,
    loading,
    error,
  };
};

export const useSensorAlertAnalytics = () => {
  const [sensorAlertCount, setSensorAlertCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = (await API.graphql({
        query: LIST_SENSOR_ALERTS,
        variables: { limit: 1000 },
      })) as any;
      const alerts = result.data?.listSensorAlerts || [];

      const alertCount = alerts.filter(
        (alert: any) => alert.status === "ACTIVE" || alert.status === "OPEN",
      ).length;

      setSensorAlertCount(alertCount);
    } catch (err) {
      console.error("Error fetching sensor alert analytics:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch sensor alerts",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    sensorAlertCount,
    loading,
    error,
  };
};

export const useActiveSensorAnalytics = () => {
  const [activeSensorCount, setActiveSensorCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = (await API.graphql({
        query: LIST_SENSORS,
        variables: { limit: 1000 },
      })) as any;
      const sensors = result.data?.listSensors || [];

      const activeCount = sensors.filter(
        (sensor: any) =>
          sensor.status === "ACTIVE" || sensor.status === "ONLINE",
      ).length;

      setActiveSensorCount(activeCount);
    } catch (err) {
      console.error("Error fetching active sensor analytics:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch active sensors",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    activeSensorCount,
    loading,
    error,
  };
};

export const useSensorHealthAnalytics = () => {
  const { activeSensorCount: totalActiveCount, loading: activeSensorsLoading, error: activeSensorsError } = useActiveSensorAnalytics();
  const { sensorAlertCount, loading: alertsLoading, error: alertsError } = useSensorAlertAnalytics();
  const { sensorErrorCount, loading: errorsLoading, error: errorsError } = useSensorErrorAnalytics();

  const activeSensorCount = Math.max(0, totalActiveCount - sensorAlertCount);
  const alertingSensorCount = sensorAlertCount;
  const erroringSensorCount = sensorErrorCount;

  const loading = activeSensorsLoading || alertsLoading || errorsLoading;
  
  const errors = [activeSensorsError, alertsError, errorsError].filter(Boolean);
  const error = errors.length > 0 ? errors.join("; ") : null;

  return {
    activeSensorCount,
    alertingSensorCount,
    erroringSensorCount,
    loading,
    error,
  };
};

export const useSensorDistributionAnalytics = () => {
  const [sensorDistribution, setSensorDistribution] = useState<
    Array<{
      type: SensorType;
      value: number;
      houseCount: number;
      color: string;
    }>
  >([]);
  const [totalSensors, setTotalSensors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = (await API.graphql({
        query: LIST_SENSORS,
        variables: { limit: 1000 },
      })) as any;
      const sensors = result.data?.listSensors || [];

      const sensorTypeGroups = sensors.reduce(
        (acc: Record<string, any[]>, sensor: any) => {
          const type = sensor.type || "UNKNOWN";
          if (!acc[type]) {
            acc[type] = [];
          }
          acc[type].push(sensor);
          return acc;
        },
        {},
      );

      const sensorDistribution = Object.entries(sensorTypeGroups)
        .map(([type, sensorsOfType]) => {
          const typedSensorsOfType = sensorsOfType as any[];
          const uniqueHubIds = new Set(
            typedSensorsOfType.map((s: any) => s.hubId),
          );
          return {
            type: type as SensorType,
            value: typedSensorsOfType.length,
            houseCount: uniqueHubIds.size,
            color: SENSOR_TYPE_COLORS[type] || DEFAULT_SENSOR_TYPE_COLOR,
          };
        })
        .sort((a, b) => b.value - a.value);

      setSensorDistribution(sensorDistribution);
      setTotalSensors(sensors.length);
    } catch (err) {
      console.error("Error fetching sensor distribution analytics:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch sensor distribution",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    sensorDistribution,
    totalSensors,
    loading,
    error,
  };
};

export const useServiceRequestAnalytics = () => {
  const [openServiceRequestCount, setOpenServiceRequestCount] = useState(0);
  const [pendingServiceRequestCount, setPendingServiceRequestCount] =
    useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = (await API.graphql({
        query: LIST_SERVICE_REQUESTS,
        variables: { limit: 1000 },
      })) as any;
      const serviceRequestsData = result.data?.listServiceRequests || [];

      const openCount = serviceRequestsData.filter(
        (sr: any) => sr.status === "OPEN" || sr.status === "NEW",
      ).length;

      const pendingCount = serviceRequestsData.filter(
        (sr: any) => sr.status === "PENDING" || sr.status === "IN_PROGRESS",
      ).length;

      setOpenServiceRequestCount(openCount);
      setPendingServiceRequestCount(pendingCount);
    } catch (err) {
      console.error("Error fetching service request analytics:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch service requests",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    openServiceRequestCount,
    pendingServiceRequestCount,
    loading,
    error,
  };
};
